import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';

const TYPE_LABELS = {
  text: 'Text',
  textarea: 'Long text',
  rating: 'Rating',
  single: 'Single choice',
  multi: 'Multiple choice',
  dropdown: 'Dropdown',
  emotion: 'Emotion',
  likert: 'Likert scale',
};

/**
 * Question template (group) picker for the survey builder.
 * Select one or more groups — expanding a group previews its questions
 * (label + type). Apply adds every question of the selected groups.
 */
export default function QuestionTemplatePicker({ open, onClose, onApply, existingDefinitionIds }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setGroups(null);
    setSelected(new Set());
    setExpanded(new Set());
    void (async () => {
      try {
        const data = await apiFetch('/admin/question-groups');
        if (!cancelled) setGroups(data);
      } catch (e) {
        if (!cancelled) {
          showError(getApiErrorMessage(e, 'Failed to load question templates.'));
          setGroups([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, open, showError]);

  const existing = useMemo(() => new Set(existingDefinitionIds || []), [existingDefinitionIds]);

  function toggleSelected(gid) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }

  function toggleExpanded(gid) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }

  const selectedGroups = (groups || []).filter((g) => selected.has(g.id));
  const newQuestionCount = selectedGroups.reduce(
    (sum, g) => sum + g.questions.filter((q) => !existing.has(q.id)).length,
    0,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add questions from templates</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select one or more question groups. Every question of the selected groups is added to
          this survey — you can still edit each question afterwards for this survey only.
        </Typography>
        {groups === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No question groups yet — create groups in the question bank first.
          </Typography>
        ) : (
          <List dense disablePadding>
            {groups.map((g) => {
              const isExpanded = expanded.has(g.id);
              const freshCount = g.questions.filter((q) => !existing.has(q.id)).length;
              return (
                <React.Fragment key={g.id}>
                  <ListItem
                    disableGutters
                    sx={{
                      border: '1px solid',
                      borderColor: selected.has(g.id) ? 'text.primary' : 'divider',
                      borderRadius: '8px',
                      mb: 1,
                      px: 1,
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Checkbox
                        size="small"
                        checked={selected.has(g.id)}
                        onChange={() => toggleSelected(g.id)}
                        inputProps={{ 'aria-label': `Select group ${g.name}` }}
                      />
                      <ListItemText
                        primary={g.name}
                        secondary={
                          `${g.questions.length} question${g.questions.length === 1 ? '' : 's'}` +
                          (freshCount < g.questions.length ? ` · ${g.questions.length - freshCount} already in survey` : '')
                        }
                        onClick={() => toggleSelected(g.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(g.id)}
                        aria-label={isExpanded ? 'Hide questions' : 'Preview questions'}
                        aria-expanded={isExpanded}
                      >
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{
                            transition: 'transform 150ms ease',
                            transform: isExpanded ? 'rotate(90deg)' : 'none',
                          }}
                        />
                      </IconButton>
                    </Box>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 5, pr: 1, pb: 1 }}>
                        {g.questions.map((q) => (
                          <Box
                            key={q.id}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ flex: 1, color: existing.has(q.id) ? 'text.disabled' : 'text.primary' }}
                            >
                              {q.label}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={TYPE_LABELS[q.type] || q.type}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={newQuestionCount === 0}
          onClick={() => onApply(selectedGroups)}
          sx={{
            textTransform: 'none',
            backgroundColor: '#000000',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          Add {newQuestionCount > 0 ? `${newQuestionCount} ` : ''}question{newQuestionCount === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
